import { useCallback, useMemo, useState } from 'react';

import { useGetBroadcastClips } from '@/features/clips/api/get-broadcast-clips';
import { WHOLE_CUT_LIST_FILTERS } from '@/features/clips/api/infinite-clips-page';
import { useGetSegmentClips } from '@/features/segments/api/get-segment-clips-by-id';

import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';

/**
 * The workspaces mentioned by the cuts of the list's source, as the options of the cut list's
 * workspace filter. They are read from every cut of that source with no filter applied, so
 * ticking a workspace or searching never shrinks them, and from the cuts' own mentions, which are
 * what `workspace_id__in` matches — the broadcast's segments may mention others.
 *
 * Nothing is requested until the dropdown opens. While it is open, whatever source the list
 * moves to is requested; once closed, only the source it was last open on stays requested, so
 * stepping through segments with the panel open costs no extra request per segment. Every clip
 * mutation invalidates `['clips']`, which keeps a requested list current.
 */
export function useCutsActiveWorkspaces() {
  const { cutsListSource } = useBroadcastScheduleProgram();
  const kind = cutsListSource?.kind;
  const id = cutsListSource?.id ?? '';
  const sourceKey = cutsListSource ? `${kind}:${id}` : null;

  // RISK: relies on the control reporting every close through `onMenuClose`, as the design system
  // does from 2.443.0 on. A close it left unreported would keep `isOpen` true, and every segment
  // the list moved to would be requested until the next open and close.
  const [isOpen, setIsOpen] = useState(false);
  const [requestedFor, setRequestedFor] = useState<string | null>(null);
  const enabled = sourceKey !== null && (isOpen || requestedFor === sourceKey);

  // The programme's query is the one the global view reads, so both share one cache entry.
  const programQuery = useGetBroadcastClips({
    id: kind === 'program' ? id : '',
    filters: WHOLE_CUT_LIST_FILTERS,
    config: { enabled: enabled && kind === 'program' },
  });
  const segmentQuery = useGetSegmentClips({
    id: kind === 'segment' ? id : '',
    filters: WHOLE_CUT_LIST_FILTERS,
    config: { enabled: enabled && kind === 'segment' },
  });
  const query = kind === 'segment' ? segmentQuery : programQuery;

  const ids = useMemo(() => {
    if (!enabled || !query.data) return undefined;
    const workspaces = query.data.data.flatMap(clip =>
      (clip.clip_mentions ?? []).flatMap(mention => mention.workspaces ?? [])
    );
    return [...new Set(workspaces)];
  }, [enabled, query.data]);

  const isError = enabled && query.isError;
  // Loading is "ids not known yet", never `isPending`: a disabled query stays pending forever.
  const isFetching = (enabled && query.isFetching) || (ids === undefined && !isError);

  const onOpen = useCallback(() => setIsOpen(true), []);
  const onClose = useCallback(() => {
    setIsOpen(false);
    setRequestedFor(sourceKey);
  }, [sourceKey]);

  return { ids, isFetching, isError, onOpen, onClose };
}
